import { configureStore, createSlice } from '@reduxjs/toolkit';

// ── Constants ────────────────────────────────────────────────────────────────
export const MM_TO_PX = 3.7795275591;
export const CM_TO_PX = MM_TO_PX * 10;
export const CANVAS_PAD = 32; // padding du canvas-shell (px)
export const SIZER_PAD = 80; // marge autour du document dans la zone scroll

export const PRESETS = {
  A4: { w: 210, h: 297 },
  A5: { w: 148, h: 210 },
  A6: { w: 105, h: 148 },
  Letter: { w: 215.9, h: 279.4 },
  Instagram: { w: 1080 / MM_TO_PX, h: 1080 / MM_TO_PX },
  Twitter: { w: 1600 / MM_TO_PX, h: 900 / MM_TO_PX },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
export const uid = () => 'id_' + Math.random().toString(36).slice(2, 10);
export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
export const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
export const toColor = (v) =>
  /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v || '') ? v : '';
export const labelFor = (type) =>
  ({ text: 'Texte', rect: 'Rectangle', circle: 'Cercle', image: 'Image' }[
    type
  ] || 'Élément');
export const escapeHtml = (str) =>
  String(str).replace(
    /[&<>"]/g,
    (s) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[s])
  );
export const formatDate = (iso) => {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const fitImage = (sw, sh, tw, th, mode = 'contain') => {
  if (mode === 'cover') {
    const r = Math.max(tw / sw, th / sh);
    return { x: (tw - sw * r) / 2, y: (th - sh * r) / 2, w: sw * r, h: sh * r };
  }
  const r = Math.min(tw / sw, th / sh);
  return { x: (tw - sw * r) / 2, y: (th - sh * r) / 2, w: sw * r, h: sh * r };
};

// ── Initial state factories ──────────────────────────────────────────────────
export const makePage = (name = 'Page 1') => ({
  id: uid(),
  name,
  background: null,
  elements: [],
});

export const makeElement = (type, extra = {}) => {
  const base = {
    id: uid(),
    type,
    x: 40,
    y: 40,
    w: 180,
    h: 100,
    z: 1,
    fill: '#e5eef7',
    stroke: '#0D3B66',
    strokeWidth: 1,
    borderStyle: 'solid',
    radius: 0,
    rotation: 0,
    opacity: 1,
    locked: false,
    visible: true,
    name: labelFor(type),
  };
  if (type === 'circle')
    Object.assign(base, { w: 120, h: 120, fill: '#FF8A3A22' });
  if (type === 'text')
    Object.assign(base, {
      w: 240,
      h: 80,
      text: 'Double-cliquez pour éditer',
      color: '#111827',
      fontFamily: 'Marianne',
      fontSize: 26,
      fontWeight: '500',
      fill: 'transparent',
      stroke: 'transparent',
      strokeWidth: 0,
    });
  return Object.assign(base, extra);
};

const initialDoc = {
  widthPx: Math.round(210 * MM_TO_PX),
  heightPx: Math.round(297 * MM_TO_PX),
  widthMm: 210,
  heightMm: 297,
};

const initialState = {
  // Document
  doc: initialDoc,
  pages: [makePage('Page 1')],
  currentPage: 0,
  documentId: null,
  documentName: 'Sans titre',

  // Canvas
  zoom: 1,
  showCropMarks: true,
  showRulers: false,
  isFullscreen: false,

  // Selection
  selection: [],
  bgSelected: false,
  lastSelectedId: null,

  // History
  history: [],
  historyIndex: -1,

  // Database (merge fields)
  db: { fields: [], records: [], currentRecord: 0 },

  // Save state: 'idle' | 'saving' | 'saved' | 'error'
  saveStatus: 'idle',
  saveError: null,
};

// ── Slice ────────────────────────────────────────────────────────────────────
const studioSlice = createSlice({
  name: 'studio',
  initialState,
  reducers: {
    // ── Document ──
    setDoc(state, { payload }) {
      state.doc = payload;
    },
    setDocumentMeta(state, { payload: { id, name } }) {
      if (id !== undefined) state.documentId = id;
      if (name !== undefined) state.documentName = name;
    },
    loadDocumentContent(state, { payload }) {
      state.doc = payload.doc;
      state.pages = payload.pages;
      state.showCropMarks = payload.showCropMarks ?? true;
      state.db = payload.db || { fields: [], records: [], currentRecord: 0 };
      state.documentId = payload.documentId || null;
      state.documentName = payload.documentName || 'Sans titre';
      state.currentPage = 0;
      state.selection = [];
      state.bgSelected = false;
    },
    newDocument(state, { payload: { mmW, mmH } }) {
      state.doc = {
        widthPx: Math.round(mmW * MM_TO_PX),
        heightPx: Math.round(mmH * MM_TO_PX),
        widthMm: mmW,
        heightMm: mmH,
      };
      state.pages = [makePage('Page 1')];
      state.currentPage = 0;
      state.selection = [];
      state.lastSelectedId = null;
      state.documentId = null;
    },

    // ── Pages ──
    addPage(state) {
      const name = `Page ${state.pages.length + 1}`;
      state.pages.push(makePage(name));
      state.currentPage = state.pages.length - 1;
    },
    setCurrentPage(state, { payload }) {
      state.currentPage = payload;
      state.selection = [];
      state.bgSelected = false;
    },
    renamePage(state, { payload: { index, name } }) {
      state.pages[index].name = name;
    },
    duplicatePage(state, { payload: index }) {
      const copy = deepCopy(state.pages[index]);
      copy.id = uid();
      copy.name = `${copy.name} (copie)`;
      copy.elements.forEach((e) => {
        e.id = uid();
      });
      state.pages.splice(index + 1, 0, copy);
      state.currentPage = index + 1;
    },
    deletePage(state, { payload: index }) {
      state.pages.splice(index, 1);
      state.currentPage = Math.max(
        0,
        Math.min(state.currentPage, state.pages.length - 1)
      );
    },
    updateBackground(state, { payload }) {
      state.pages[state.currentPage].background = payload;
    },

    // ── Elements ──
    addElement(state, { payload }) {
      const page = state.pages[state.currentPage];
      const z = page.elements.length + 1;
      const elem = makeElement(payload.type, { ...payload.extra, z });
      page.elements.push(elem);
      // re-index z
      page.elements.forEach((e, i) => {
        e.z = i + 1;
      });
      state.selection = [elem.id];
      state.lastSelectedId = elem.id;
    },
    updateElement(state, { payload: { id, changes } }) {
      const page = state.pages[state.currentPage];
      const elem = page.elements.find((e) => e.id === id);
      if (elem) Object.assign(elem, changes);
    },
    updateElements(state, { payload }) {
      // payload: [{ id, changes }]
      const page = state.pages[state.currentPage];
      payload.forEach(({ id, changes }) => {
        const elem = page.elements.find((e) => e.id === id);
        if (elem) Object.assign(elem, changes);
      });
    },
    deleteSelected(state) {
      const ids = new Set(state.selection);
      const page = state.pages[state.currentPage];
      page.elements = page.elements.filter((e) => !ids.has(e.id));
      page.elements.forEach((e, i) => {
        e.z = i + 1;
      });
      state.selection = [];
    },
    reorderLayers(state, { payload: { draggedId, targetId } }) {
      const elems = state.pages[state.currentPage].elements;
      const from = elems.findIndex((e) => e.id === draggedId);
      const to = elems.findIndex((e) => e.id === targetId);
      if (from < 0 || to < 0 || from === to) return;
      const [moved] = elems.splice(from, 1);
      elems.splice(to, 0, moved);
      elems.forEach((e, i) => {
        e.z = i + 1;
      });
    },

    // ── Selection ──
    setSelection(state, { payload }) {
      state.selection = payload;
      state.lastSelectedId = payload[payload.length - 1] || null;
      state.bgSelected = false;
    },
    toggleSelection(state, { payload: { id, useRange } }) {
      const ids = state.pages[state.currentPage].elements.map((e) => e.id);
      if (
        useRange &&
        state.lastSelectedId &&
        ids.includes(state.lastSelectedId)
      ) {
        const a = ids.indexOf(state.lastSelectedId);
        const b = ids.indexOf(id);
        const [min, max] = [Math.min(a, b), Math.max(a, b)];
        state.selection = [
          ...new Set([...state.selection, ...ids.slice(min, max + 1)]),
        ];
      } else {
        if (state.selection.includes(id)) {
          state.selection = state.selection.filter((x) => x !== id);
        } else {
          state.selection = [...state.selection, id];
          state.lastSelectedId = id;
        }
      }
    },
    setBgSelected(state, { payload }) {
      state.bgSelected = payload;
      if (payload) state.selection = [];
    },
    clearSelection(state) {
      state.selection = [];
      state.bgSelected = false;
    },

    // ── Canvas ──
    setZoomLevel(state, { payload }) {
      state.zoom = clamp(payload, 0.1, 4);
    },
    setShowCropMarks(state, { payload }) {
      state.showCropMarks = payload;
    },
    setShowRulers(state, { payload }) {
      state.showRulers = payload;
    },
    setFullscreen(state, { payload }) {
      state.isFullscreen = payload;
    },

    // ── History ──
    pushHistory(state) {
      const snapshot = deepCopy({
        doc: state.doc,
        pages: state.pages,
        currentPage: state.currentPage,
        showCropMarks: state.showCropMarks,
      });
      state.history = state.history.slice(0, state.historyIndex + 1);
      state.history.push(snapshot);
      if (state.history.length > 80) state.history.shift();
      state.historyIndex = state.history.length - 1;
    },
    restoreHistory(state, { payload: index }) {
      if (index < 0 || index >= state.history.length) return;
      const snap = deepCopy(state.history[index]);
      state.doc = snap.doc;
      state.pages = snap.pages;
      state.currentPage = snap.currentPage;
      state.showCropMarks = snap.showCropMarks;
      state.selection = [];
      state.historyIndex = index;
    },

    // ── DB ──
    setDb(state, { payload }) {
      state.db = payload;
    },
    dbAddField(state, { payload: name }) {
      if (state.db.fields.includes(name)) return;
      state.db.fields.push(name);
      state.db.records.forEach((r) => {
        if (r[name] === undefined) r[name] = '';
      });
    },
    dbRemoveField(state, { payload: name }) {
      state.db.fields = state.db.fields.filter((f) => f !== name);
      state.db.records.forEach((r) => {
        delete r[name];
      });
      state.pages.forEach((p) =>
        p.elements.forEach((e) => {
          if (e.dbField === name) e.dbField = null;
        })
      );
    },
    dbRenameField(state, { payload: { oldName, newName } }) {
      const idx = state.db.fields.indexOf(oldName);
      if (
        idx < 0 ||
        !newName ||
        newName === oldName ||
        state.db.fields.includes(newName)
      )
        return;
      state.db.fields[idx] = newName;
      state.db.records.forEach((r) => {
        r[newName] = r[oldName] ?? '';
        delete r[oldName];
      });
      state.pages.forEach((p) =>
        p.elements.forEach((e) => {
          if (e.dbField === oldName) e.dbField = newName;
        })
      );
    },
    dbAddRecord(state) {
      const rec = {};
      state.db.fields.forEach((f) => {
        rec[f] = '';
      });
      state.db.records.push(rec);
      state.db.currentRecord = state.db.records.length - 1;
    },
    dbRemoveRecord(state, { payload: idx }) {
      state.db.records.splice(idx, 1);
      state.db.currentRecord = Math.max(
        0,
        Math.min(state.db.currentRecord, state.db.records.length - 1)
      );
    },
    dbUpdateCell(state, { payload: { row, field, value } }) {
      if (state.db.records[row]) state.db.records[row][field] = value;
    },
    dbSetCurrentRecord(state, { payload }) {
      state.db.currentRecord = payload;
    },
    dbImportCsv(state, { payload: { fields, records } }) {
      state.db.fields = fields;
      state.db.records = records;
      state.db.currentRecord = 0;
    },

    // ── Save status ──
    setSaveStatus(state, { payload: { status, error } }) {
      state.saveStatus = status;
      state.saveError = error || null;
    },
  },
});

export const studioActions = studioSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const sel = {
  doc: (s) => s.studio.doc,
  pages: (s) => s.studio.pages,
  currentPageIdx: (s) => s.studio.currentPage,
  currentPage: (s) => s.studio.pages[s.studio.currentPage],
  zoom: (s) => s.studio.zoom,
  selection: (s) => s.studio.selection,
  bgSelected: (s) => s.studio.bgSelected,
  showCropMarks: (s) => s.studio.showCropMarks,
  showRulers: (s) => s.studio.showRulers,
  isFullscreen: (s) => s.studio.isFullscreen,
  history: (s) => s.studio.history,
  historyIndex: (s) => s.studio.historyIndex,
  db: (s) => s.studio.db,
  documentId: (s) => s.studio.documentId,
  documentName: (s) => s.studio.documentName,
  saveStatus: (s) => s.studio.saveStatus,
  saveError: (s) => s.studio.saveError,
  selectedElements: (s) => {
    const page = s.studio.pages[s.studio.currentPage];
    return page
      ? page.elements.filter((e) => s.studio.selection.includes(e.id))
      : [];
  },
};

// ── Store ────────────────────────────────────────────────────────────────────
export const studioStore = configureStore({
  reducer: { studio: studioSlice.reducer },
  middleware: (getDefault) => getDefault({ serializableCheck: false }),
});
